import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import FilteredTableCore from "../../components/Organismos/Table/FilteredTableCore";
import { type IHeader } from "../../components/Organismos/Table/FilteredTable.types";
import {
  FIELDS_TO_IGNORE,
  getExcelColumnTitle,
} from "../../components/Organismos/Table/FilteredTable.utils";
import DropDownMultiple, {
  type DropDownOptionBase,
} from "../../components/staff/DropDownMultiple";
import Button from "../../components/staff/Button";
import DropdownButton from "../../components/Atomos/Buttons/DropdownButton";
import Gear from "../../components/Atomos/Icons/Gear";
import Log from "../../components/Atomos/Icons/Log";
import Play from "../../components/Atomos/Icons/Play";
import IIntegratorFirm from "../../interfaces/models/IIntegratorFirm";
import IIntegratorRunLog from "../../interfaces/models/IIntegratorRunLog";
import {
  getFirmDashboardCatalog,
  getFirmsByIds,
  type FirmSyncDashboardLookup,
} from "../../Services/Firm";
import { getIntegratorFirms } from "../../Services/IntegratorFirm";
import {
  getLatestIntegratorRunLogs,
  runIntegratorBatch,
  runIntegratorImport,
} from "../../Services/IntegratorRunLog";
import { defaultPageSize } from "../../utils/const";
import { formatTimestampDateTime } from "../../utils/dates";
import { toastError, toastInfo, toastSuccess } from "../../utils/toast";
import {
  buildActiveSyncFirmRows,
  type ShowFirmsSelection,
} from "./activeSyncFirmRows";
import GeneralSettingsModal from "./GeneralSettingsModal";
import LastRunDateCell from "./LastRunDateCell";
import UploadFeedModal, { type UploadFeedPayload } from "./UploadFeedModal";

const LOADING_FIRMS_MESSAGE = "Loading firms…";
const NO_ACTIVE_SYNC_MESSAGE = "No active sync configurations are present.";
const NO_SEARCH_MATCH_MESSAGE = "No firms found";

const SHOW_FIRMS_DEFAULT: ShowFirmsSelection = "configured";
const SHOW_FIRMS_OPTIONS: DropDownOptionBase[] = [
  { id: "configured", name: "Configured" },
  { id: "not-configured", name: "Not configured" },
  { id: "all", name: "All" },
];

function rejectedMessage(reason: unknown, fallback: string): string {
  if (reason instanceof Error && reason.message.trim()) {
    return reason.message;
  }
  return fallback;
}

const ACTION_BUTTON_CLASS =
  "!w-auto shrink-0 whitespace-nowrap !px-3 !py-1.5 text-sm";

/** The Run control never changes its label, so why it is disabled is told by the tooltip. */
function runButtonTooltip(
  hasIntegratorConfig: boolean,
  runningFirmId: string | null,
  firmId: string,
  isBatchRunning: boolean
): string {
  if (runningFirmId === firmId) {
    return "This firm's import is running…";
  }
  if (!hasIntegratorConfig) {
    return "Configure this firm's feed before running it";
  }
  if (isBatchRunning) {
    return "A batch is being queued";
  }
  if (runningFirmId) {
    return "Another firm's import is running";
  }
  return "Run Integrator Engine for this Firm";
}

export default function IntegratorEngine(): ReactElement {
  const navigate = useNavigate();
  const endpoint = "/firm";

  // Null until the configuration list has been read. The grid is that list, not `/firm`.
  const [configs, setConfigs] = useState<IIntegratorFirm[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [firmCatalog, setFirmCatalog] = useState<FirmSyncDashboardLookup[]>([]);
  const [showFirms, setShowFirms] = useState<ShowFirmsSelection>(SHOW_FIRMS_DEFAULT);
  const [latestByFirmId, setLatestByFirmId] = useState<
    Map<string, IIntegratorRunLog>
  >(new Map());

  // Firm whose run is in flight. Only one at a time from this screen: the backend rejects
  // a second run of the same firm with a 409, and one at a time keeps it obvious which
  // firm a toast belongs to.
  const [runningFirmId, setRunningFirmId] = useState<string | null>(null);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uploadFirmId, setUploadFirmId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [configOutcome, catalogOutcome] = await Promise.allSettled([
        getIntegratorFirms(),
        getFirmDashboardCatalog(),
      ]);
      if (cancelled) {
        return;
      }
      if (configOutcome.status === "rejected") {
        const message = rejectedMessage(
          configOutcome.reason,
          "Could not load sync configurations."
        );
        setLoadError(message);
        toastError(message);
        return;
      }
      const nextConfigs = configOutcome.value;
      let nextCatalog =
        catalogOutcome.status === "fulfilled" ? catalogOutcome.value : [];
      if (catalogOutcome.status === "rejected") {
        toastError(
          rejectedMessage(catalogOutcome.reason, "Could not load firms.")
        );
      }

      if (nextConfigs.length === 0) {
        setFirmCatalog(nextCatalog);
        setConfigs(nextConfigs);
        setLatestByFirmId(new Map());
        return;
      }

      const firmIds = nextConfigs.map((config) => config.firmId);
      const [firmResult, logResult] = await Promise.allSettled([
        nextCatalog.length > 0
          ? Promise.resolve([])
          : getFirmsByIds(firmIds),
        getLatestIntegratorRunLogs(firmIds),
      ]);
      if (cancelled) {
        return;
      }
      if (nextCatalog.length === 0 && firmResult.status === "fulfilled") {
        nextCatalog = firmResult.value;
      }
      if (firmResult.status === "rejected") {
        toastError(rejectedMessage(firmResult.reason, "Could not load firms."));
      }
      const nextLogs =
        logResult.status === "fulfilled"
          ? new Map(logResult.value.map((log) => [log.firmId, log]))
          : new Map<string, IIntegratorRunLog>();
      if (logResult.status === "rejected") {
        toastError(
          rejectedMessage(logResult.reason, "Could not load execution logs.")
        );
      }
      setFirmCatalog(nextCatalog);
      setLatestByFirmId(nextLogs);
      setConfigs(nextConfigs);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo(
    () =>
      configs === null
        ? []
        : buildActiveSyncFirmRows({
            configurations: configs,
            firms: firmCatalog,
            latestLogs: [...latestByFirmId.values()],
            showFirms,
          }),
    [configs, firmCatalog, latestByFirmId, showFirms]
  );

  const showFirmsOption = useMemo(
    () =>
      SHOW_FIRMS_OPTIONS.find((option) => option.id === showFirms) ??
      SHOW_FIRMS_OPTIONS[0],
    [showFirms]
  );

  const hasEnabledFirm = useMemo(
    () => (configs ?? []).some((config) => config.enabled === true),
    [configs]
  );

  const isBusy = runningFirmId !== null || isBatchRunning;

  const handleRunImport = useCallback(
    async (
      firmId: string,
      options: { force?: boolean; feedData?: string; fileName?: string } = {}
    ) => {
      if (isBusy) {
        return;
      }
      setRunningFirmId(firmId);
      try {
        // The run nests its log: the response also carries whether the report was mailed.
        const log = (await runIntegratorImport(firmId, options))?.runLog;
        if (log) {
          setLatestByFirmId((prev) => {
            const next = new Map(prev);
            next.set(log.firmId, log);
            return next;
          });
        }
        // A failed run also answers 200: the outcome is in `status`, not in the response code.
        if (log?.status === "success") {
          // A run that reached the end succeeded, whatever each integrator managed to
          // write: what was inserted, updated or failed belongs in the report, not here.
          toastSuccess("Import completed.");
        } else if (log?.status === "skipped") {
          toastInfo("Feed unchanged since the last run — nothing new was stored.");
        } else {
          toastError(log?.errorMessage ?? "The run finished with an error.", {
            autoClose: 6000,
          });
        }
      } catch (e: any) {
        // Only what stopped the run from being recorded reaches here: 400 / 404 / 409.
        toastError(e?.message ?? "Could not run the import.", { autoClose: 6000 });
      } finally {
        setRunningFirmId(null);
      }
    },
    [isBusy]
  );

  const handleRunBatch = useCallback(async () => {
    if (isBusy) {
      return;
    }
    setIsBatchRunning(true);
    try {
      const result = await runIntegratorBatch();
      const jobs = result?.jobs ?? [];
      const failures = result?.failures ?? [];

      if (jobs.length > 0) {
        setLatestByFirmId((prev) => {
          const next = new Map(prev);
          for (const job of jobs) {
            next.set(job.firmId, job);
          }
          return next;
        });
        toastSuccess(
          jobs.length === 1
            ? "Batch queued for 1 firm."
            : `Batch queued for ${jobs.length} firms.`
        );
      } else {
        toastInfo("No enabled firms were queued.");
      }

      if (failures.length > 0) {
        toastError(
          failures.length === 1
            ? `1 firm could not be queued: ${failures[0].errorMessage}`
            : `${failures.length} firms could not be queued.`,
          { autoClose: 6000 }
        );
      }
    } catch (e: any) {
      toastError(e?.message ?? "Could not queue the batch.", { autoClose: 6000 });
    } finally {
      setIsBatchRunning(false);
    }
  }, [isBusy]);

  const columnsDef: IHeader[] = useMemo(
    () => [
      { id: "firmName", header: "Firm" },
      { id: "regionNames", header: "Region", defaultVisibility: false },
      { id: "membershipStatus", header: "Membership Status", defaultVisibility: false },
      { id: "integratorEnabled", header: "Enabled", enableSorting: false, width: 110 },
      { id: "feedUrl", header: "URL", enableSorting: false, width: 280 },
      {
        id: "latestImport",
        header: "Latest Import",
        width: 210,
        cell: ({ row }) => {
          if (row.latestImport == null) {
            return <></>;
          }
          return (
            <LastRunDateCell
              date={formatTimestampDateTime(row.latestImport)}
              status={row.latestRunLog?.status}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        // Split Log control is h-8; keep the four actions on one line.
        width: 400,
        cell: ({ row }) => (
          <div className="flex flex-nowrap items-center justify-start gap-2 whitespace-nowrap">
            <DropdownButton
              color="neutral"
              style="outlined"
              action={{
                label: "Log",
                disabled: !row.latestRunLog,
                onClick: () => {
                  if (!row.latestRunLog) {
                    return;
                  }
                  navigate(`/integrator-engine/run-logs/${row.latestRunLog.id}`, {
                    state: row.latestRunLog,
                  });
                },
              }}
              items={[
                {
                  id: "all-logs",
                  label: "All Logs",
                  onSelect: () =>
                    navigate(`/integrator-engine/run-logs?firmId=${row.id}`),
                },
              ]}
            />
            <Button
              text="Configure"
              color="secondary"
              variant="filled"
              size="fixed"
              className={ACTION_BUTTON_CLASS}
              onClick={() =>
                navigate(`/integrator-engine/firms/${row.id}`, { state: row })
              }
            />
            <Button
              text="Professionals"
              color="neutral"
              variant="outlined"
              size="fixed"
              className={ACTION_BUTTON_CLASS}
              tooltip="Review the firm's professionals by segment"
              onClick={() => navigate(`/integrator-engine/professionals/${row.id}`)}
            />
            {/* Always rendered so every row keeps the same actions; only enabled when the firm
                has IntegratorFirm config. The endpoint still rejects disabled firms. */}
            <div
              title={runButtonTooltip(
                row.hasIntegratorConfig,
                runningFirmId,
                row.id,
                isBatchRunning
              )}
            >
              <DropdownButton
                color="accent"
                style="outlined"
                disabled={!row.hasIntegratorConfig || isBusy}
                action={{
                  label: "Run",
                  onClick: () => void handleRunImport(row.id),
                }}
                items={[
                  {
                    id: "force-run",
                    label: "Run (force)",
                    onSelect: () => void handleRunImport(row.id, { force: true }),
                  },
                  {
                    id: "upload",
                    label: "Upload",
                    onSelect: () => setUploadFirmId(row.id),
                  },
                ]}
              />
            </div>
          </div>
        ),
      },
    ],
    [navigate, runningFirmId, isBusy, isBatchRunning, handleRunImport]
  );

  const excelHeaders = useMemo(
    () =>
      columnsDef
        .filter((col) => !FIELDS_TO_IGNORE.has(col.id))
        .map((col) => getExcelColumnTitle(col)),
    [columnsDef]
  );

  const handleUploadConfirm = useCallback(
    async (payload: UploadFeedPayload) => {
      if (!uploadFirmId) {
        return;
      }
      await handleRunImport(uploadFirmId, payload);
      setUploadFirmId(null);
    },
    [uploadFirmId, handleRunImport]
  );

  return (
    <div className="flex flex-col min-w-0">
      <div className="flex w-full items-center justify-end gap-4 rounded-t-lg bg-accent-Default-dark px-6 py-3">
        <Button
          text="Run Batch"
          color="white"
          variant="outlined"
          size="fixed"
          className="!w-auto shrink-0 whitespace-nowrap"
          disabled={isBusy || !hasEnabledFirm}
          tooltip={
            isBatchRunning
              ? "The batch is being queued…"
              : runningFirmId
                ? "A firm's import is already running"
                : hasEnabledFirm
                  ? "Queue an import for every enabled firm"
                  : "No enabled firms to queue"
          }
          icon={<Play />}
          onClick={() => void handleRunBatch()}
        />
        <Button
          text="All Logs"
          color="white"
          variant="outlined"
          size="fixed"
          className="!w-auto shrink-0 whitespace-nowrap"
          icon={<Log />}
          onClick={() => navigate("/integrator-engine/run-logs")}
        />
        <Button
          text="General Settings"
          color="white"
          variant="outlined"
          size="fixed"
          className="!w-auto shrink-0 whitespace-nowrap"
          icon={<Gear />}
          onClick={() => setSettingsOpen(true)}
        />
      </div>

      <GeneralSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
      />

      <UploadFeedModal
        isOpen={uploadFirmId != null}
        onClose={() => setUploadFirmId(null)}
        onConfirm={handleUploadConfirm}
      />

      <FilteredTableCore
        columnsDef={columnsDef}
        showRowNumber
        endpoint={endpoint}
        clientData={rows}
        filtersData={[]}
        searchLeading={
          <div className="w-full max-w-xs shrink-0 sm:w-72">
            <DropDownMultiple<DropDownOptionBase>
              title="Show Firms:"
              options={SHOW_FIRMS_OPTIONS}
              value={showFirmsOption}
              onChange={(option) => {
                const next = option ? String(option.id) : SHOW_FIRMS_DEFAULT;
                if (
                  next === "configured" ||
                  next === "not-configured" ||
                  next === "all"
                ) {
                  setShowFirms(next);
                }
              }}
              searchable={false}
            />
          </div>
        }
        excelHeaders={excelHeaders}
        excelFilename="integrator-firms"
        tableKey="import-module-dashboard"
        persistFilters={false}
        defaultSorting={{ field: "latestImport", order: "desc" }}
        seedDefaultSortingPreference
        defaultPageSize={defaultPageSize}
        showSearchInput
        showSelectedFilters={false}
        inlineFilterToolbar
        searchPlaceholder="Search by firm name or Short ID"
        emptyStateMessage={
          loadError
            ? loadError
            : configs === null
              ? LOADING_FIRMS_MESSAGE
              : showFirms === "configured" && configs.length === 0
                ? NO_ACTIVE_SYNC_MESSAGE
                : NO_SEARCH_MATCH_MESSAGE
        }
      />
    </div>
  );
}
